import React from 'react';
import clsx from 'clsx';
import {emitEventLog} from '@di-shared/features/analytics';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import BatteryCoach from '@di-shared/features/home/kr/ev-battery/participatory-care/BatteryCoach';
import ParticipatoryEvBatteryCareCharging from '@di-shared/features/home/kr/ev-battery/participatory-care/ParticipatoryEvBatteryCareCharging';
import ParticipatoryEvBatteryCareDriving from '@di-shared/features/home/kr/ev-battery/participatory-care/ParticipatoryEvBatteryCareDriving';

enum BatteryGuideTab {
  DRIVING = 'DRIVING',
  CHARGING = 'CHARGING',
}

interface ParticipatoryEvBatteryCareProps {
  tab?: BatteryGuideTab;
  vin: string;
  puid: string;
}

function ParticipatoryEvBatteryCare({tab, vin, puid}: ParticipatoryEvBatteryCareProps): JSX.Element {
  const {t} = useInferableTranslation();
  const [selectedTab, setSelectedTab] = React.useState<BatteryGuideTab>(tab ?? BatteryGuideTab.DRIVING);

  return (
    <section className="section section_battery_guide">
      <div className="section_container_header">
        <h3 className="title_xxsmall">{t('T30399')}</h3>
        <span className="logo">
          <ImageSelector
            kiaLightSrc={() => import('@di-shared/assets/img/home/hyundai/casper_electric_logo_light.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/home/hyundai/casper_electric_logo.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/casper_electric_logo_light.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/casper_electric_logo.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/home/hyundai/casper_electric_logo_light.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/home/hyundai/casper_electric_logo.png')}
            alt={t('T30430')}
            width="79"
            height="29"
          />
        </span>
      </div>

      <div
        className="tabbar type_brick"
        role="tablist"
      >
        <button
          type="button"
          className={clsx('tabbar_item body_medium_b', {'is-selected': selectedTab === BatteryGuideTab.DRIVING})}
          onClick={() => {
            emitEventLog({
              event: 'di_battery-coach_tabs',
              data_name: 'driving',
            });
            setSelectedTab(BatteryGuideTab.DRIVING);
          }}
          id={`${BatteryGuideTab.DRIVING}_subtab`}
          role="tab"
          aria-selected={selectedTab === BatteryGuideTab.DRIVING}
          aria-controls={`${BatteryGuideTab.DRIVING}_subtabpanel`}
          tabIndex={selectedTab === BatteryGuideTab.DRIVING ? 0 : -1}
        >
          {t('T30400')}
        </button>
        <button
          type="button"
          className={clsx('tabbar_item body_medium_b', {'is-selected': selectedTab === BatteryGuideTab.CHARGING})}
          onClick={() => {
            emitEventLog({
              event: 'di_battery-coach_tabs',
              data_name: 'charging',
            });
            setSelectedTab(BatteryGuideTab.CHARGING);
          }}
          id={`${BatteryGuideTab.CHARGING}_subtab`}
          role="tab"
          aria-selected={selectedTab === BatteryGuideTab.CHARGING}
          aria-controls={`${BatteryGuideTab.CHARGING}_subtabpanel`}
          tabIndex={selectedTab === BatteryGuideTab.CHARGING ? 0 : -1}
        >
          {t('T10065')}
        </button>
      </div>
      <div className="tab_content">
        <div className="section_container">
          {selectedTab === BatteryGuideTab.DRIVING && (
            <ParticipatoryEvBatteryCareDriving
              vin={vin}
              puid={puid}
            />
          )}
          {selectedTab === BatteryGuideTab.CHARGING && (
            <ParticipatoryEvBatteryCareCharging
              vin={vin}
              puid={puid}
            />
          )}
          <BatteryCoach
            vin={vin}
            puid={puid}
          />
        </div>
      </div>
    </section>
  );
}

export default ParticipatoryEvBatteryCare;
